import type { ComponentType } from '../../simulator-page/components/circuitTypes';

export type Difficulty = 'beginner' | 'intermediate' | 'advanced';
export type ExerciseStatus = 'not-started' | 'in-progress' | 'completed' | 'failed';

export interface ExerciseStep {
  id: string;
  instruction: string;
  completed: boolean;
}

export interface RequiredComponent {
  type: ComponentType;
  count: number;
  label: string;
}

export interface Exercise {
  id: string;
  title: string;
  subtitle: string;
  difficulty: Difficulty;
  status: ExerciseStatus;
  description: string;
  learningObjectives: string[];
  requiredComponents: RequiredComponent[];
  steps: ExerciseStep[];
  hint: string;
  regulation: string;
  estimatedTime: number; // minutes
  tags: string[];
}

export const EXERCISES: Exercise[] = [
  {
    id: 'ex-one-way-lighting',
    title: 'One-Way Lighting Circuit',
    subtitle: 'Single switch controlling one lamp',
    difficulty: 'beginner',
    status: 'not-started',
    description:
      'Wire a basic one-way lighting circuit: a supply feeds through an MCB, then through a single-pole switch, to a lamp. The switch must be in the live conductor. This is the most fundamental domestic lighting circuit.',
    learningObjectives: [
      'Understand how a single-pole switch interrupts the live conductor',
      'Correctly identify and connect L, N, and E terminals',
      'Apply correct UK wire colours throughout',
      'Understand why the switch must NOT be in the neutral',
    ],
    requiredComponents: [
      { type: 'supply', count: 1, label: 'Supply (230V)' },
      { type: 'mcb', count: 1, label: 'MCB (6A)' },
      { type: 'switch-one-way', count: 1, label: '1-Way Switch' },
      { type: 'lamp', count: 1, label: 'Lamp / Luminaire' },
      { type: 'neutral-bar', count: 1, label: 'Neutral Bar' },
      { type: 'earth-bar', count: 1, label: 'Earth Bar' },
    ],
    steps: [
      { id: 'step-1w-1', instruction: 'Place a Supply component on the canvas', completed: false },
      { id: 'step-1w-2', instruction: 'Place an MCB to the right of the supply', completed: false },
      { id: 'step-1w-3', instruction: 'Connect Supply L → MCB IN with a brown (live) wire', completed: false },
      { id: 'step-1w-4', instruction: 'Place a 1-Way Switch and connect MCB OUT → Switch COM (brown)', completed: false },
      { id: 'step-1w-5', instruction: 'Place a Lamp and connect Switch L1 → Lamp L (brown)', completed: false },
      { id: 'step-1w-6', instruction: 'Place a Neutral Bar. Connect Supply N → Neutral Bar. Connect Neutral Bar → Lamp N (blue)', completed: false },
      { id: 'step-1w-7', instruction: 'Place an Earth Bar. Connect Supply E → Earth Bar. Connect Earth Bar → Lamp E (green/yellow)', completed: false },
    ],
    hint: 'Remember: the switch MUST be in the live (brown) conductor. If it is in the neutral, the lamp holder will be live even when the switch is off — a shock hazard when changing the bulb.',
    regulation: 'BS 7671 Regulation 132.14.1 — Switching must be in the live conductor only.',
    estimatedTime: 10,
    tags: ['lighting', 'switch', 'one-way', 'beginner'],
  },
  {
    id: 'ex-two-way-lighting',
    title: 'Two-Way Lighting Circuit',
    subtitle: 'Two switches controlling one lamp (staircase wiring)',
    difficulty: 'intermediate',
    status: 'not-started',
    description:
      'Wire a two-way lighting circuit where two switches at different locations (e.g. top and bottom of stairs) both control a single lamp. Uses two 2-way switches with L1 and L2 "strapping" wires between them.',
    learningObjectives: [
      'Understand the two-way switch mechanism (COM, L1, L2)',
      'Wire the strapping conductors between two 2-way switches',
      'Identify the correct terminals for each wire colour',
      'Understand why this circuit requires 3-core cable between the switches',
    ],
    requiredComponents: [
      { type: 'supply', count: 1, label: 'Supply (230V)' },
      { type: 'mcb', count: 1, label: 'MCB (6A)' },
      { type: 'switch-two-way', count: 2, label: '2-Way Switch ×2' },
      { type: 'lamp', count: 1, label: 'Lamp / Luminaire' },
      { type: 'neutral-bar', count: 1, label: 'Neutral Bar' },
      { type: 'earth-bar', count: 1, label: 'Earth Bar' },
    ],
    steps: [
      { id: 'step-2w-1', instruction: 'Place Supply, MCB, and two 2-Way Switches on the canvas', completed: false },
      { id: 'step-2w-2', instruction: 'Connect Supply L → MCB IN (brown)', completed: false },
      { id: 'step-2w-3', instruction: 'Connect MCB OUT → Switch 1 COM (brown)', completed: false },
      { id: 'step-2w-4', instruction: 'Connect Switch 1 L1 → Switch 2 L1 (strappers — can use brown)', completed: false },
      { id: 'step-2w-5', instruction: 'Connect Switch 1 L2 → Switch 2 L2 (strappers)', completed: false },
      { id: 'step-2w-6', instruction: 'Connect Switch 2 COM → Lamp L (brown)', completed: false },
      { id: 'step-2w-7', instruction: 'Connect neutral bar to Supply N and Lamp N (blue)', completed: false },
      { id: 'step-2w-8', instruction: 'Connect earth bar to Supply E and Lamp E (green/yellow)', completed: false },
    ],
    hint: 'The two "strapping" conductors (L1–L1 and L2–L2) between the two switches carry live potential. In practice, these are the red and yellow cores of a 3-core and earth cable.',
    regulation: 'BS 7671 Appendix 4 — Two-way and intermediate switching.',
    estimatedTime: 20,
    tags: ['lighting', 'two-way', 'staircase', 'intermediate'],
  },
  {
    id: 'ex-radial-socket',
    title: 'Radial Socket Circuit',
    subtitle: '20A radial circuit feeding socket outlets',
    difficulty: 'intermediate',
    status: 'not-started',
    description:
      'Wire a 20A radial socket circuit. The circuit feeds from the consumer unit (via an MCB) to socket outlets in a single radial run. Each socket must have live, neutral, and earth connections. No ring is formed.',
    learningObjectives: [
      'Understand the difference between radial and ring final circuits',
      'Wire a socket outlet with correct L, N, E connections',
      'Understand that the earth on a socket is mandatory',
      'Know that a radial circuit terminates at the last socket — it is not looped back',
    ],
    requiredComponents: [
      { type: 'supply', count: 1, label: 'Supply (230V)' },
      { type: 'mcb', count: 1, label: 'MCB (20A)' },
      { type: 'socket', count: 2, label: 'Socket Outlet ×2' },
      { type: 'neutral-bar', count: 1, label: 'Neutral Bar' },
      { type: 'earth-bar', count: 1, label: 'Earth Bar' },
    ],
    steps: [
      { id: 'step-rad-1', instruction: 'Place Supply and MCB on the canvas', completed: false },
      { id: 'step-rad-2', instruction: 'Connect Supply L → MCB IN (brown)', completed: false },
      { id: 'step-rad-3', instruction: 'Place two Socket Outlets in a row', completed: false },
      { id: 'step-rad-4', instruction: 'Connect MCB OUT → Socket 1 L terminal (brown)', completed: false },
      { id: 'step-rad-5', instruction: 'Connect Socket 1 L → Socket 2 L (brown) — radial loop-in', completed: false },
      { id: 'step-rad-6', instruction: 'Connect Neutral Bar → Socket 1 N → Socket 2 N (blue)', completed: false },
      { id: 'step-rad-7', instruction: 'Connect Earth Bar → Socket 1 E → Socket 2 E (green/yellow)', completed: false },
    ],
    hint: 'A radial circuit does NOT loop back to the supply. It starts at the MCB and ends at the last socket. This distinguishes it from a ring final circuit.',
    regulation: 'BS 7671 Regulation 433.1 — Overcurrent protection for radial circuits.',
    estimatedTime: 15,
    tags: ['sockets', 'radial', 'power', 'intermediate'],
  },
  {
    id: 'ex-ring-final',
    title: 'Ring Final Circuit',
    subtitle: '32A ring circuit — both ends return to the consumer unit',
    difficulty: 'advanced',
    status: 'not-started',
    description:
      'Wire a ring final circuit: the live, neutral, and earth conductors start at the consumer unit, feed through socket outlets in a ring, and return to the same circuit breaker. This is the most common socket circuit in UK domestic installations.',
    learningObjectives: [
      'Understand why a ring circuit provides two parallel paths for current',
      'Correctly wire both ends of the ring back to the MCB/RCBO',
      'Understand that all three conductors (L, N, E) must form a ring',
      'Know how to test ring continuity (end-to-end resistance)',
    ],
    requiredComponents: [
      { type: 'supply', count: 1, label: 'Supply (230V)' },
      { type: 'rcbo', count: 1, label: 'RCBO (32A)' },
      { type: 'socket', count: 3, label: 'Socket Outlet ×3' },
      { type: 'neutral-bar', count: 1, label: 'Neutral Bar' },
      { type: 'earth-bar', count: 1, label: 'Earth Bar' },
    ],
    steps: [
      { id: 'step-ring-1', instruction: 'Place Supply, RCBO, and 3 Sockets on the canvas', completed: false },
      { id: 'step-ring-2', instruction: 'Connect Supply L → RCBO L-IN, Supply N → RCBO N-IN', completed: false },
      { id: 'step-ring-3', instruction: 'Connect RCBO L-OUT → Socket 1 L (start of ring, brown)', completed: false },
      { id: 'step-ring-4', instruction: 'Continue ring: Socket 1 L → Socket 2 L → Socket 3 L → RCBO L-OUT (ring returns!)', completed: false },
      { id: 'step-ring-5', instruction: 'Wire neutral ring: RCBO N-OUT → Socket 1 N → Socket 2 N → Socket 3 N → RCBO N-OUT (blue)', completed: false },
      { id: 'step-ring-6', instruction: 'Wire earth ring: Earth Bar → all socket E terminals in ring → Earth Bar', completed: false },
      { id: 'step-ring-7', instruction: 'Connect Supply E → Earth Bar (green/yellow)', completed: false },
    ],
    hint: 'The key feature of a ring circuit is that both ends of each conductor (L, N, and E) return to the same terminals in the consumer unit. This halves the effective resistance and allows a 32A breaker to serve the ring.',
    regulation: 'BS 7671 Regulation 433.1.1 and Appendix 15 — Ring final circuits.',
    estimatedTime: 25,
    tags: ['sockets', 'ring-final', 'power', 'advanced'],
  },
];